import * as Sentry from "@sentry/react";

/**
 * Performance monitoring utilities for tracking key metrics
 */

// Track component render times
export const trackComponentRender = (componentName: string, duration: number) => {
  Sentry.addBreadcrumb({
    message: `Component ${componentName} rendered`,
    data: { duration },
    level: "info",
  });

  // Track slow renders (>500ms)
  if (duration > 500) {
    Sentry.captureMessage(`Slow render: ${componentName} took ${duration}ms`, "warning");
  }
};

// Track data fetching performance
export const trackDataFetch = async <T>(
  operation: string,
  fetchFn: () => Promise<T>
): Promise<T> => {
  return await Sentry.startSpan(
    {
      name: `data.fetch.${operation}`,
      op: "http.client",
    },
    async () => {
      const startTime = performance.now();
      
      try {
        const result = await fetchFn();
        const duration = performance.now() - startTime;
        
        Sentry.addBreadcrumb({
          message: `Data fetch: ${operation}`,
          data: { duration },
          level: "info",
        });
        
        // Track slow queries (>3s)
        if (duration > 3000) {
          Sentry.captureMessage(
            `Slow data fetch: ${operation} took ${duration}ms`,
            "warning"
          );
        }
        
        return result;
      } catch (error) {
        Sentry.captureException(error, {
          contexts: {
            custom: {
              operation,
              duration: performance.now() - startTime,
            },
          },
        });
        throw error;
      }
    }
  );
};

// Track user interactions
export const trackInteraction = (action: string, data?: Record<string, any>) => {
  Sentry.addBreadcrumb({
    message: `User interaction: ${action}`,
    data,
    level: "info",
  });
};

// Monitor Web Vitals
export const setupWebVitalsMonitoring = () => {
  if (typeof window === "undefined") return;

  // Track Largest Contentful Paint (LCP)
  const lcpObserver = new PerformanceObserver((list) => {
    const entries = list.getEntries();
    const lastEntry = entries[entries.length - 1];
    
    Sentry.addBreadcrumb({
      message: "Web Vital: LCP",
      data: { value: lastEntry.startTime },
      level: "info",
    });
    
    if (lastEntry.startTime > 2500) {
      Sentry.captureMessage(
        `Poor LCP: ${lastEntry.startTime}ms (should be <2.5s)`,
        "warning"
      );
    }
  });
  
  try {
    lcpObserver.observe({ type: "largest-contentful-paint", buffered: true });
  } catch (e) {
    // LCP observer not supported
  }

  // Track First Input Delay (FID)
  const fidObserver = new PerformanceObserver((list) => {
    const entries = list.getEntries();
    entries.forEach((entry: any) => {
      const fid = entry.processingStart - entry.startTime;
      
      Sentry.addBreadcrumb({
        message: "Web Vital: FID",
        data: { value: fid },
        level: "info",
      });
      
      if (fid > 100) {
        Sentry.captureMessage(
          `Poor FID: ${fid}ms (should be <100ms)`,
          "warning"
        );
      }
    });
  });
  
  try {
    fidObserver.observe({ type: "first-input", buffered: true });
  } catch (e) {
    // FID observer not supported
  }

  // Track Cumulative Layout Shift (CLS)
  let clsValue = 0;
  const clsObserver = new PerformanceObserver((list) => {
    list.getEntries().forEach((entry: any) => {
      if (!entry.hadRecentInput) {
        clsValue += entry.value;
      }
    });
    
    Sentry.addBreadcrumb({
      message: "Web Vital: CLS",
      data: { value: clsValue },
      level: "info",
    });
    
    if (clsValue > 0.1) {
      Sentry.captureMessage(
        `Poor CLS: ${clsValue} (should be <0.1)`,
        "warning"
      );
    }
  });
  
  try {
    clsObserver.observe({ type: "layout-shift", buffered: true });
  } catch (e) {
    // CLS observer not supported
  }
};

// Track memory usage (if available)
export const monitorMemoryUsage = () => {
  if (typeof window === "undefined" || !(performance as any).memory) return;

  const memory = (performance as any).memory;
  const usedMemoryMB = memory.usedJSHeapSize / 1048576;
  const totalMemoryMB = memory.jsHeapSizeLimit / 1048576;
  const usagePercent = (usedMemoryMB / totalMemoryMB) * 100;

  Sentry.addBreadcrumb({
    message: "Memory usage",
    data: {
      used: `${usedMemoryMB.toFixed(2)}MB`,
      total: `${totalMemoryMB.toFixed(2)}MB`,
      percentage: `${usagePercent.toFixed(2)}%`,
    },
    level: "info",
  });

  // Alert if memory usage is high (>80%)
  if (usagePercent > 80) {
    Sentry.captureMessage(
      `High memory usage: ${usagePercent.toFixed(2)}%`,
      "warning"
    );
  }
};

// Setup periodic memory monitoring
export const startMemoryMonitoring = (intervalMs: number = 60000) => {
  monitorMemoryUsage();
  return setInterval(monitorMemoryUsage, intervalMs);
};
